import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { DateField } from '@/components/form/fields/date/DateInput';
import { ConfirmField } from '@/components/form/fields/text/ConfirmInput';
import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useProduct, useProducts, useReplaceProduct } from '@/services/api/hooks/product';
import { zodResolver } from '@hookform/resolvers/zod';

import { format } from 'date-fns';
import { FunctionComponent, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';
import ReplaceProductSearch from './ReplaceProductSearch';

const ReplaceProductFormFields = z.object({
  effDate: z.date(),
  isConfirmed: z.literal(true),
  replacementProductId: z.string()
});

type ReplaceProductFormFields = z.infer<typeof ReplaceProductFormFields>;

const ReplaceProductModal: FunctionComponent<ModalProps<{ productId: string }>> = props => {
  const HideProduct = () => {
    if (!props.props?.productId) throw new Error('No product ID provided');
    const navigate = useNavigate();
    const [product] = useProduct(props.props.productId);
    const [products] = useProducts();
    const methods = useForm<ReplaceProductFormFields>({ resolver: zodResolver(ReplaceProductFormFields) });

    const replaceProduct = useReplaceProduct();

    const onSubmit = async (data: ReplaceProductFormFields) => {
      const replacementProduct = products.find(product => product.id === data.replacementProductId);

      await replaceProduct.mutateAsync({
        replacedProductId: product.id,
        replacementProductId: data.replacementProductId,
        effectiveFrom: data.effDate
      });

      navigate(`/configuratorCatalogue/${product.category.id}`);

      toast(
        `Product: ${product.name} has been replaced with ${replacementProduct?.name}, effective ${format(
          data.effDate,
          'dd/MM/yyyy'
        )}`,
        {
          duration: 5000
        }
      );
    };

    const errorObj = replaceProduct.error as Error | undefined;
    const error = errorObj ? errorObj.message : '';

    return (
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Replace product</h1>
        </Modal.Controls>
        <div className="mt-1 space-y-2">
          <p>
            Replacing a product will cause it to be updated in all catalogues and bundles it is used when the effective
            date is passed. MSPs & customers will be notified of this change in advance.
          </p>
        </div>
        <Form className="flex w-full flex-col gap-3 py-3" methods={methods} onSubmit={onSubmit}>
          <ReplaceProductSearch fieldId={'replacementProductId'} />
          <DateField label={'Effective from'} id={'effDate'} />
          <ConfirmField
            label={`Confirm by typing the name of ${product.name}`}
            id="isConfirmed"
            target={product.name}
          />

          {error && <p className="text-red-500">{error}</p>}

          <div className="flex justify-end space-x-2">
            <Button type="button" className="w-fit" variant={ButtonVariant.Mono} onClick={() => props.close()}>
              Cancel
            </Button>
            <Button
              loading={replaceProduct.isLoading}
              className="w-fit"
              variant={ButtonVariant.Destructive}
              type="submit"
            >
              Confirm
            </Button>
          </div>
        </Form>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <HideProduct />
      </Suspense>
    </Modal>
  );
};

export default ReplaceProductModal;
